import { useCallback } from "react";
import type { CustomerLoginRouterScope } from "@Front/Components/Layouts/Login/helpers/customerLoginRouterScope";
import {
	decideCustomerLoginAfterSingleOfficeScope,
	filterCustomerCandidatesByOfficeUid,
} from "@Front/Components/Layouts/Login/helpers/loginCustomerMultiCandidateRouting";
import type { CustomerLoginState } from "@Front/Components/Layouts/Login/hooks/helpers/loginStateHook";
import { applyCustomerLoginMulticandidateDecision } from "@Front/Components/Layouts/Login/hooks/helpers/loginEmailSubmissionExecution";
import { mailVerifyScopeFromLoginState, withDisambiguationBusy } from "@Front/Components/Layouts/Login/hooks/helpers/loginDisambiguationScopeHelpers";

async function applyOfficeSelectionToLoginFlow(
	officeUid: string,
	loginScope: CustomerLoginRouterScope,
	loginState: CustomerLoginState,
): Promise<void> {
	const { email, loginCandidates, setAuthDisambiguation, setStep, setValidationErrors, setCustomerTargetChoiceCandidates } = loginState;
	if (!email || !loginCandidates) {
		return;
	}
	const filtered = filterCustomerCandidatesByOfficeUid(loginCandidates.customerCandidates, officeUid);
	let decision = decideCustomerLoginAfterSingleOfficeScope(filtered);
	if (decision.kind === "mail_verify" && loginCandidates.actorChoiceRequired) {
		decision = {
			kind: "mail_verify",
			disambiguation: { ...decision.disambiguation, loginActor: "CUSTOMER" },
		};
	}
	const scope = mailVerifyScopeFromLoginState(loginScope, loginState);
	await applyCustomerLoginMulticandidateDecision(decision, email, {
		...scope,
		setStep,
		setValidationErrors,
		setAuthDisambiguation,
		setCustomerTargetChoiceCandidates,
	});
}

export function useCustomerOfficeSelectHandler(
	loginScope: CustomerLoginRouterScope,
	loginState: CustomerLoginState,
	setDisambiguationBusy: (v: boolean) => void,
): (officeUid: string) => Promise<void> {
	return useCallback(
		async (officeUid: string) => {
			await withDisambiguationBusy(setDisambiguationBusy, async () => {
				await applyOfficeSelectionToLoginFlow(officeUid, loginScope, loginState);
			});
		},
		[loginScope, loginState, setDisambiguationBusy],
	);
}
